type Pt = {x: number; y: number}

/** centroid por fórmula (funciona para polígonos simples). Fallback para média quando area ~ 0 */
export function polygonCentroid(pts: Pt[]): Pt {
  const n = pts.length
  if (n === 0) return {x: 0, y: 0}

  let area = 0
  let cx = 0
  let cy = 0
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    const a = pts[i].x * pts[j].y - pts[j].x * pts[i].y
    area += a
    cx += (pts[i].x + pts[j].x) * a
    cy += (pts[i].y + pts[j].y) * a
  }
  area *= 0.5
  if (Math.abs(area) < 1e-9) {
    // degenerado: usa média simples
    const sum = pts.reduce((acc, p) => ({x: acc.x + p.x, y: acc.y + p.y}), {x: 0, y: 0})
    return {x: sum.x / n, y: sum.y / n}
  }
  cx /= 6 * area
  cy /= 6 * area
  return {x: cx, y: cy}
}

/** cross test: >eps => left, < -eps => right, otherwise 0 (colinear) */
export function sideOfLine(p1: Pt, p2: Pt, q: Pt, eps = 1e-6): number {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  const wx = q.x - p1.x
  const wy = q.y - p1.y
  const cross = dx * wy - dy * wx
  if (cross > eps) return 1
  if (cross < -eps) return -1
  return 0
}

export function getSidesPolygons(points: {x: number; y: number}[], dim: {width: number; height: number; x?: number; y?: number}) {
  if (!points || points.length !== 2) return null
  const eps = 1e-6
  const [p1, p2] = points

  const x0 = typeof dim.x === 'number' ? dim.x : 0
  const y0 = typeof dim.y === 'number' ? dim.y : 0
  const w = dim.width
  const h = dim.height

  // cantos em sentido horário
  const corners = [
    {x: x0, y: y0}, // top-left
    {x: x0 + w, y: y0}, // top-right
    {x: x0 + w, y: y0 + h}, // bottom-right
    {x: x0, y: y0 + h}, // bottom-left
  ]

  const borders = [
    [corners[0], corners[1]], // top
    [corners[1], corners[2]], // right
    [corners[2], corners[3]], // bottom
    [corners[3], corners[0]], // left
  ]

  // Interseção entre a reta AB (infinita) e o segmento CD (borda)
  function intersectLineWithSegment(A: any, B: any, C: any, D: any) {
    const A1 = B.y - A.y
    const B1 = A.x - B.x
    const C1 = A1 * A.x + B1 * A.y

    const A2 = D.y - C.y
    const B2 = C.x - D.x
    const C2 = A2 * C.x + B2 * C.y

    const det = A1 * B2 - A2 * B1
    if (Math.abs(det) < eps) return null // paralelas (ou quase)

    const x = (B2 * C1 - B1 * C2) / det
    const y = (A1 * C2 - A2 * C1) / det

    // precisa estar sobre o segmento CD (borda)
    const withinSegment = (p: any, q: any, v: any) => {
      return v.x >= Math.min(p.x, q.x) - 1e-2 && v.x <= Math.max(p.x, q.x) + 1e-2 && v.y >= Math.min(p.y, q.y) - 1e-2 && v.y <= Math.max(p.y, q.y) + 1e-2
    }
    const pt = {x, y}
    if (withinSegment(C, D, pt)) return pt
    return null
  }

  // coletar interseções (reta × cada uma das 4 bordas)
  let intersections: {x: number; y: number}[] = []
  for (const [C, D] of borders) {
    const i = intersectLineWithSegment(p1, p2, C, D)
    if (i) {
      // dedupe por proximidade
      if (!intersections.some((q) => Math.hypot(q.x - i.x, q.y - i.y) < 1e-3)) {
        intersections.push(i)
      }
    }
  }

  // se não achou 2 interseções, não dá para dividir corretamente
  if (intersections.length < 2) return null

  // se por acaso achou > 2 (ex: passando por canto), escolhe os 2 mais distantes
  if (intersections.length > 2) {
    let best: {i: number; j: number; d: number} = {i: 0, j: 1, d: -1}
    for (let i = 0; i < intersections.length; i++) {
      for (let j = i + 1; j < intersections.length; j++) {
        const d = Math.hypot(intersections[i].x - intersections[j].x, intersections[i].y - intersections[j].y)
        if (d > best.d) best = {i, j, d}
      }
    }
    intersections = [intersections[best.i], intersections[best.j]]
  }

  const [i1, i2] = intersections

  // achar índice da borda onde cada interseção está
  function borderIndexOf(pt: {x: number; y: number}) {
    for (let idx = 0; idx < borders.length; idx++) {
      const [C, D] = borders[idx]
      if (pt.x >= Math.min(C.x, D.x) - 1e-2 && pt.x <= Math.max(C.x, D.x) + 1e-2 && pt.y >= Math.min(C.y, D.y) - 1e-2 && pt.y <= Math.max(C.y, D.y) + 1e-2) {
        return idx
      }
    }
    return -1
  }
  const b1 = borderIndexOf(i1)
  const b2 = borderIndexOf(i2)
  if (b1 === -1 || b2 === -1) return null

  // constrói polígono que vai de iStart (na borda bStart) até iEnd (na borda bEnd)
  function buildPolyFromBorders(iStart: {x: number; y: number}, bStart: number, iEnd: {x: number; y: number}, bEnd: number) {
    const poly: {x: number; y: number}[] = []
    poly.push(iStart)
    let idx = bStart
    // se start e end na mesma borda: só adiciona iEnd e pronto
    if (bStart === bEnd) {
      poly.push(iEnd)
      return poly
    }
    while (true) {
      const cornerToPush = corners[(idx + 1) % corners.length]
      poly.push(cornerToPush)
      if (idx === bEnd) {
        // ao alcançar a borda do end, substituímos o canto final por iEnd
        poly.pop() // remove a esquina que está além de iEnd
        poly.push(iEnd)
        break
      }
      idx = (idx + 1) % corners.length
    }
    return poly
  }

  const polyA = buildPolyFromBorders(i1, b1, i2, b2)
  const polyB = buildPolyFromBorders(i2, b2, i1, b1)

  return {left: polyA, right: polyB}
}

/**
 * Recebe o resultado de getSidesPolygons (sides.left / sides.right),
 * e garante qual é direita/esquerda com base no cross (p1->p2).
 */
export function assignPolygonsByCross(sides: {left: Pt[]; right: Pt[]}, p1: Pt, p2: Pt): {blue: Pt[]} {
  const cLeft = polygonCentroid(sides.left)
  const cRight = polygonCentroid(sides.right)

  let sLeft = sideOfLine(p1, p2, cLeft)
  let sRight = sideOfLine(p1, p2, cRight)

  // se algum centróide colinear, tenta uma pequena perturbação perpendicular
  if (sLeft === 0 || sRight === 0) {
    const dx = p2.x - p1.x
    const dy = p2.y - p1.y
    const norm = Math.hypot(dx, dy) || 1
    const px = -dy / norm // perp unit
    const py = dx / norm

    if (sLeft === 0) {
      const tryPt = {x: cLeft.x + px * 1e-3, y: cLeft.y + py * 1e-3}
      sLeft = sideOfLine(p1, p2, tryPt)
    }
    if (sRight === 0) {
      const tryPt = {x: cRight.x + px * 1e-3, y: cRight.y + py * 1e-3}
      sRight = sideOfLine(p1, p2, tryPt)
    }
  }

  // agora sLeft e sRight devem ser opostos (1 e -1). Se ainda não, fallback pela comparação dos X/Y do centróide:
  if (sLeft === 0 && sRight === 0) {
    // fallback robusto: compara projeção do centróide no normal da linha
    const dx = p2.x - p1.x
    const dy = p2.y - p1.y
    const mid = {x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2}
    // vetor normal (perp)
    const nx = -dy
    const ny = dx
    const projLeft = (cLeft.x - mid.x) * nx + (cLeft.y - mid.y) * ny
    if (projLeft > 0) {
      return {blue: sides.left}
    } else {
      return {blue: sides.right}
    }
  }

  // sLeft > 0 => left-of-line, sLeft < 0 => right-of-line
  if (sLeft > 0) {
    return {blue: sides.left}
  } else {
    return {blue: sides.right}
  }
}
